import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Robot, Download, Play, StopCircle, Copy, ArrowClockwise,
  CheckCircle, XCircle, Clock, Hourglass, UploadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = window.location.origin;

function StatusBadge({ status, dispatched }) {
  const real = status === 'pending' && dispatched ? 'paid_pending' : status;
  const map = {
    pending:      { cls: 'bg-gray-100 text-gray-700', icon: <Clock size={12} /> , label: 'Queued' },
    running:      { cls: 'bg-blue-100 text-blue-700', icon: <ArrowClockwise size={12} className="animate-spin" />, label: 'Running' },
    success:      { cls: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle size={12} weight="fill" />, label: 'Success' },
    failed:       { cls: 'bg-red-100 text-red-700', icon: <XCircle size={12} weight="fill" />, label: 'Failed' },
    paid_pending: { cls: 'bg-amber-100 text-amber-800', icon: <Hourglass size={12} />, label: 'Awaiting App Approval' },
  };
  const cfg = map[real] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded ${cfg.cls}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

export default function SemiAutoTab() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [workerToken, setWorkerToken] = useState('');
  const pollRef = useRef(null);

  // Form state
  const [provider, setProvider] = useState('indane');
  const [lpgText, setLpgText] = useState('');
  const [parallel, setParallel] = useState(5);
  const [autoGc, setAutoGc] = useState(true);
  const [creating, setCreating] = useState(false);

  const loadSessions = useCallback(async () => {
    try {
      const r = await api.get('/automation/semi-auto/sessions');
      setSessions(r.data || []);
    } catch (e) {
      /* ignore */
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const r = await api.get(`/automation/semi-auto/sessions/${id}`);
      setDetail(r.data);
      setWorkerToken(r.data?.worker_token || '');
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (!activeId) return undefined;
    loadDetail(activeId);
    pollRef.current = setInterval(() => loadDetail(activeId), 4000);
    return () => clearInterval(pollRef.current);
  }, [activeId, loadDetail]);

  const createSession = async () => {
    const ids = lpgText.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean);
    if (!ids.length) { toast.error('Kam se kam 1 LPG ID daalo'); return; }
    setCreating(true);
    try {
      const r = await api.post('/automation/semi-auto/create', {
        provider,
        lpg_ids: ids,
        parallel: Math.min(20, Math.max(1, Number(parallel) || 5)),
        auto_assign_gift_cards: autoGc,
      });
      toast.success(r.data.message);
      setLpgText('');
      setActiveId(r.data.session_id);
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const stopSession = async () => {
    if (!activeId) return;
    if (!window.confirm('Session stop karna hai?')) return;
    try {
      await api.put(`/automation/semi-auto/sessions/${activeId}/control?action=stop`);
      toast.success('Session stopped');
      await loadDetail(activeId);
    } catch (e) {
      toast.error('Stop failed');
    }
  };

  const downloadWorker = () => {
    const a = document.createElement('a');
    a.href = `${API}/api/automation/semi-auto/download-worker`;
    a.download = 'lpg_semi_auto.py';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(
        `/automation/semi-auto/upload-excel?parallel=${parallel}&auto_assign_gift_cards=${autoGc}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(r.data.message);
      await loadSessions();
      const first = r.data.sessions?.[0]?.session_id;
      if (first) setActiveId(first);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Excel upload failed');
    }
  };

  const runCommand = activeId && workerToken
    ? `python3 lpg_semi_auto.py --session ${activeId} --worker-token ${workerToken} --parallel ${detail?.parallel || parallel}`
    : '';

  const copyCmd = () => {
    if (!runCommand) return;
    navigator.clipboard.writeText(runCommand);
    toast.success('Command copied');
  };

  return (
    <div className="space-y-4" data-testid="semi-auto-tab">
      {/* Header + worker download */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-semibold text-indigo-900 flex items-center gap-2">
            <Robot size={22} weight="duotone" className="text-indigo-600" />
            Semi-Auto Booking — 20 parallel tabs
          </div>
          <div className="text-xs text-indigo-800 mt-1 max-w-2xl">
            Amazon login manual karo (pehli baar), profile save ho jaata hai. Phir LPG IDs auto-fill honge,
            payment page tak pahunch jayega. Aap mobile app se approve karke success/pending status
            yahi dekh sakte ho.
          </div>
        </div>
        <Button onClick={downloadWorker} variant="outline" className="gap-2" data-testid="download-semi-auto-worker">
          <Download size={16} /> Download Worker Script
        </Button>
      </div>

      {!activeId && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold text-gray-900">Create New Booking Session</div>
            <label className="inline-flex items-center gap-2 text-xs font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded-lg border border-indigo-200 cursor-pointer transition" data-testid="excel-upload-label">
              <UploadSimple size={14} weight="bold" />
              Upload Excel (LPG_Provider, Consumer_Number, Amount)
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcelUpload} data-testid="excel-upload-input" />
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_140px] gap-3">
            <div>
              <Label className="text-xs">LPG IDs (one per line or comma-separated)</Label>
              <Textarea
                rows={6}
                value={lpgText}
                onChange={(e) => setLpgText(e.target.value)}
                placeholder={'12345678901\n22334455667\n...'}
                data-testid="semi-auto-lpg-textarea"
              />
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Provider</Label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger data-testid="semi-auto-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="indane">Indane</SelectItem>
                    <SelectItem value="bharat">Bharat Gas</SelectItem>
                    <SelectItem value="hp">HP Gas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Parallel Tabs (1-20)</Label>
                <Input
                  type="number" min={1} max={20}
                  value={parallel}
                  onChange={(e) => setParallel(e.target.value)}
                  data-testid="semi-auto-parallel"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={autoGc}
                  onChange={(e) => setAutoGc(e.target.checked)}
                  data-testid="semi-auto-auto-gc"
                />
                Auto-assign unused Gift Cards
              </label>
            </div>
            <div className="flex items-end">
              <Button
                onClick={createSession}
                disabled={creating}
                className="w-full bg-indigo-600 hover:bg-indigo-700 gap-2"
                data-testid="semi-auto-create-btn"
              >
                <Play size={16} weight="bold" />
                {creating ? 'Creating...' : 'Create Session'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeId && detail && (
        <div className="space-y-4">
          {/* Session header */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs text-gray-500">Active Session</div>
                <div className="font-semibold text-gray-900">{detail.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Provider: <b>{detail.provider}</b> • Parallel: <b>{detail.parallel}</b> • Status: <b>{detail.status}</b>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setActiveId(null); setDetail(null); loadSessions(); }}>
                  Back
                </Button>
                {detail.status === 'running' && (
                  <Button variant="outline" size="sm" className="text-red-600 gap-1" onClick={stopSession} data-testid="stop-session-btn">
                    <StopCircle size={14} weight="bold" /> Stop
                  </Button>
                )}
              </div>
            </div>

            {/* Run command */}
            <div className="mt-4 bg-gray-900 text-gray-100 rounded-lg p-3 flex items-center justify-between gap-2 font-mono text-xs">
              <code className="truncate" data-testid="semi-auto-run-cmd">{runCommand}</code>
              <button onClick={copyCmd} className="text-gray-300 hover:text-white shrink-0" data-testid="copy-cmd-btn">
                <Copy size={14} />
              </button>
            </div>
            <div className="text-[11px] text-gray-500 mt-2">
              Apne laptop pe yeh command chalayein. Pehli baar me Amazon me login karein, profile save ho jaata hai.
            </div>

            {/* Counts */}
            <div className="grid grid-cols-5 gap-2 mt-4">
              {[
                ['pending', 'Queued', 'bg-gray-50 text-gray-700'],
                ['running', 'Running', 'bg-blue-50 text-blue-700'],
                ['paid_pending', 'Awaiting App', 'bg-amber-50 text-amber-800'],
                ['success', 'Success', 'bg-emerald-50 text-emerald-700'],
                ['failed', 'Failed', 'bg-red-50 text-red-700'],
              ].map(([k, lbl, cls]) => (
                <div key={k} className={`rounded-lg p-3 ${cls}`} data-testid={`count-${k}`}>
                  <div className="text-[11px] uppercase tracking-wider">{lbl}</div>
                  <div className="text-xl font-bold">{detail.counts?.[k] || 0}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tasks table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-2 border-b text-xs text-gray-500 flex items-center justify-between">
              <span>Tasks — auto-updates every 4s</span>
              <ArrowClockwise size={12} className="animate-spin text-gray-400" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">#</th>
                    <th className="px-3 py-2 text-left">LPG ID</th>
                    <th className="px-3 py-2 text-left">Gift Card</th>
                    <th className="px-3 py-2 text-left">Step</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Order URL</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.tasks || []).map((t) => (
                    <tr key={t.index} className="border-t hover:bg-gray-50" data-testid={`semi-auto-task-row-${t.index}`}>
                      <td className="px-3 py-2 text-gray-500">{t.index + 1}</td>
                      <td className="px-3 py-2 font-mono text-xs">{t.lpg_id}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-600">
                        {t.gift_card_code ? t.gift_card_code.slice(0, 12) + '…' : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{t.current_step || '—'}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {t.bill_amount ? `₹${t.bill_amount.toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <StatusBadge status={t.status} dispatched={t.dispatched} />
                        {t.error_message && (
                          <div className="text-[10px] text-red-600 mt-1 max-w-[200px] truncate" title={t.error_message}>
                            {t.error_message}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {t.order_url ? (
                          <a href={t.order_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate max-w-[160px] inline-block">
                            View
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Sessions list */}
      {!activeId && sessions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b font-semibold text-gray-900 flex items-center justify-between">
            <span>Recent Sessions</span>
            <button onClick={loadSessions} className="text-gray-500 hover:text-gray-900" title="Refresh">
              <ArrowClockwise size={16} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Provider</th>
                  <th className="px-3 py-2 text-right">Tasks</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Created By</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t hover:bg-gray-50" data-testid={`session-row-${s.id}`}>
                    <td className="px-3 py-2 font-medium">{s.name}</td>
                    <td className="px-3 py-2 capitalize">{s.provider}</td>
                    <td className="px-3 py-2 text-right">{s.total_tasks}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${
                        s.status === 'running' ? 'bg-blue-100 text-blue-700' :
                        s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        s.status === 'stopped' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{s.status}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{s.created_by_name}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="outline" size="sm"
                        onClick={() => setActiveId(s.id)}
                        data-testid={`open-session-${s.id}`}
                      >Open</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
